import { visibleAchievements } from '../data/achievements.js';
import { patternCatalog, totalPatternCount } from '../data/patternCatalog.js';
import { audio } from '../systems/audio/AudioSystem.js';

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Persistent local profile statistics collected after each completed run. */
export class StatisticsMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay statistics-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    const stats = this.save.statistics;
    this.el.innerHTML = `
      <div class="menu-kicker">RUN ARCHIVE</div>
      <div class="menu-title">STATISTICS</div>
      <div class="menu-subtitle">${this.save.accountUsername ? `@${this.save.accountUsername}` : (this.save.profileName || 'Guest profile')}</div>
      <div class="shop-rows statistics-rows">
        <div class="shop-row"><span>Runs</span><strong>${stats.runs}</strong></div>
        <div class="shop-row"><span>Wins</span><strong>${stats.wins}</strong></div>
        <div class="shop-row"><span>Losses</span><strong>${stats.losses}</strong></div>
        <div class="shop-row"><span>Normal clears</span><strong>${stats.normalWins}</strong></div>
        <div class="shop-row"><span>Endless runs</span><strong>${stats.endlessRuns}</strong></div>
        <div class="shop-row"><span>Bosses defeated</span><strong>${stats.bossesDefeated}</strong></div>
        <div class="shop-row"><span>Deepest gauntlet</span><strong>${stats.highestGauntletIndex < 0 ? '—' : stats.highestGauntletIndex + 1}</strong></div>
        <div class="shop-row"><span>Highest Ascension</span><strong>A${stats.highestAscension}</strong></div>
        <div class="shop-row"><span>Total damage</span><strong>${Math.floor(stats.totalDamageDealt).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Bullets grazed</span><strong>${(stats.totalGrazes ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Longest graze chain</span><strong>${(stats.bestGrazeChain ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Bullets absorbed</span><strong>${(stats.totalBulletsAbsorbed ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Longest resonance chain</span><strong>${(stats.bestResonance ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Spell cards captured</span><strong>${(stats.totalCaptures ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Echo duels won</span><strong>${(stats.totalEchoDuelsWon ?? 0).toLocaleString()}</strong></div>
        <div class="shop-row"><span>Total survival</span><strong>${formatTime(stats.totalSurvivalSeconds)}</strong></div>
        <div class="shop-row"><span>Fastest normal clear</span><strong>${stats.fastestClearSeconds === null ? '—' : formatTime(stats.fastestClearSeconds)}</strong></div>
        <div class="shop-row"><span>Best score</span><strong>${stats.bestScore.toLocaleString()}</strong></div>
      </div>
      ${this._renderCaptures()}
      ${this._renderAchievements()}
      <div class="scroll-hint">▼ Scroll to browse</div>
    `;
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  /**
   * The capture list (BULLET-HELL.md §D1): every boss, every spell card it can
   * play, and whether you have ever survived that card clean. This is the
   * cheapest breadcrumb trail in the game — the 66 named patterns were already
   * authored, so naming them here turns them into 66 goals for free, and it's
   * the reason a danmaku player can name forty attacks from a game they last
   * played twenty years ago.
   *
   * Cards never met are shown by name anyway rather than hidden: knowing that
   * The Unmade King has an attack called Apotheosis you have not yet beaten is
   * the point of the list, not a spoiler to protect. That holds WITHIN an act
   * the player has unlocked; a locked act's cards are withheld entirely (see
   * patternCatalog's `statistics` argument), because naming them is naming the
   * act, which act select and the codex both refuse to do.
   */
  _renderCaptures() {
    const records = this.save.statistics.patternRecords ?? {};
    const sections = patternCatalog(this.save.statistics).map(({ bossName, patterns }) => {
      const captured = patterns.filter((name) => (records[name]?.captures ?? 0) > 0).length;
      const rows = patterns.map((name) => {
        const record = records[name] ?? { attempts: 0, captures: 0 };
        const done = record.captures > 0;
        const detail = record.attempts
          ? `${record.captures}/${record.attempts}`
          : 'never faced';
        return `
          <div class="achievement-row${done ? ' achievement-row-unlocked' : ''}">
            <span class="achievement-name">${name}</span>
            <span class="achievement-hint">${detail}</span>
            <span class="achievement-echoes">${done ? '✓' : '—'}</span>
          </div>
        `;
      }).join('');
      return `
        <div class="achievement-group-heading">${bossName} <small>${captured}/${patterns.length}</small></div>
        ${rows}
      `;
    }).join('');

    const total = totalPatternCount(this.save.statistics);
    const capturedNames = Object.values(records).filter((r) => (r?.captures ?? 0) > 0).length;
    return `
      <div class="menu-kicker achievements-kicker">SPELL CARDS ${Math.min(capturedNames, total)}/${total}</div>
      <div class="menu-subtitle statistics-captures-note">Survive a boss attack start to finish without being hit to capture it.</div>
      <div class="shop-rows achievement-rows capture-rows">${sections}</div>
    `;
  }

  /**
   * The breadcrumb trail (BULLET-HEAVEN.md §H6 — machine #8): every
   * achievement, unlocked and locked alike, with the locked ones showing what
   * it would take. Locked entries are the whole point — the list exists to
   * make sure there is always a named next thing one or two runs away, and a
   * list that only shows what you've already done can't do that.
   *
   * Unlocked ones are listed first within each group so the list reads as
   * progress rather than as a wall of things you haven't done, and grouped so
   * the player can see which kind of play they've been neglecting.
   */
  _renderAchievements() {
    // Re-evaluated on every open, not just at run end. That is what lets an
    // achievement added in a later version unlock retroactively from
    // statistics already on disk, rather than asking the player to re-earn
    // something they demonstrably already did. Idempotent — SaveManager's
    // unlocked ledger is the payment record.
    this.save.evaluateAchievements(null);
    const owned = new Set(this.save.unlockedAchievements);

    const groups = new Map();
    const listed = visibleAchievements(this.save.statistics);
    for (const achievement of listed) {
      const group = achievement.group ?? 'Other';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(achievement);
    }

    const sections = [...groups].map(([group, entries]) => {
      const sorted = [...entries].sort((a, b) => Number(owned.has(b.id)) - Number(owned.has(a.id)));
      const done = sorted.filter((a) => owned.has(a.id)).length;
      const rows = sorted.map((achievement) => {
        const unlocked = owned.has(achievement.id);
        return `
          <div class="achievement-row${unlocked ? ' achievement-row-unlocked' : ''}">
            <span class="achievement-name">${unlocked ? achievement.name : '???'}</span>
            <span class="achievement-hint">${achievement.hint}</span>
            <span class="achievement-echoes">${unlocked ? '✓' : `+${achievement.echoes}`}</span>
          </div>
        `;
      }).join('');
      return `
        <div class="achievement-group-heading">${group} <small>${done}/${entries.length}</small></div>
        ${rows}
      `;
    }).join('');

    return `
      <div class="menu-kicker achievements-kicker">ACHIEVEMENTS ${Math.min(owned.size, listed.length)}/${listed.length}</div>
      <div class="shop-rows achievement-rows">${sections}</div>
    `;
  }

  destroy() {
    this.el.remove();
  }
}
